import PropTypes from 'prop-types';
import React from 'react';
import styled from 'styled-components';

import Select from '../Select';
import Bubble from '../Bubble';
import Throbber from '../Throbber';

const {
  StyledSelected: StyledSelectedOption,
  StyledOptions,
  StyledOption: StyledOptionWrapper,
  StyledStatusIcon,
} = Select;

class OperatorStatusSelector extends React.PureComponent {
  static optionRenderer({ title, value, color }) {
    return (
      <StyledOption>
        <StyledOptionIndicator status={value} color={color} />
        {title}
      </StyledOption>
    );
  }

  static getDerivedStateFromProps(props, state) {
    const { value: nextForceVal } = props;
    const { value: nextVal, manualState } = state;
    const isStateChanged = manualState !== nextVal;
    const isPropChanged = manualState !== nextForceVal;

    if (isStateChanged) {
      return {
        value: nextVal,
        manualState: nextVal,
      };
    }

    if (isPropChanged) {
      return {
        manualState: nextForceVal,
        value: nextForceVal,
      };
    }

    return null;
  }

  constructor(props) {
    super(props);

    this.state = {
      value: props.value,
      manualState: props.value,
    };

    this.handleAfterService = this.handleAfterService.bind(this);
    this.handleChange = this.handleChange.bind(this);
    this.headRenderer = this.headRenderer.bind(this);
  }

  handleAfterService() {
    const { onChange, manualState } = this.props;

    this.setState(
      prevState => ({
        manualState: null,
        value: prevState.manualState,
      }),
      () => {
        onChange({
          isManual: true,
          manualState: null,
          state: manualState,
        });
      }
    );
  }

  handleChange({ detail }) {
    const { onChange } = this.props;
    const { value } = detail;
    const newData = { value, isManual: true };

    this.setState(newData, () =>
      onChange({
        isManual: true,
        state: value,
      })
    );
  }

  headRenderer(selectedOption) {
    if (!selectedOption)
      return (
        <StyledHead>
          <StyledOption>Выберите статус</StyledOption>
        </StyledHead>
      );

    const { title, color, value } = selectedOption;

    const { errors, onErrorReset } = this.props;

    const isErrorShown = !!errors.length;

    return (
      <StyledHead>
        <Bubble
          key={isErrorShown}
          title={isErrorShown && 'Внимание'}
          text={errors.join(', ')}
          type="error"
          placement="bottom"
          open={isErrorShown}
          closeable
          onClose={isErrorShown && onErrorReset}
          shouldStopPropagation={false}
        >
          <StyledOption>{title}</StyledOption>
        </Bubble>
        <StyledIndicator status={value} color={color} />
      </StyledHead>
    );
  }

  render() {
    const { value, options } = this.props;

    if (options === null)
      return (
        <StyledHead className={this.props.className || undefined}>
          <StyledOption>
            <StyledThrobber size={10} color={'rgb(38, 39, 41)'} />
          </StyledOption>
        </StyledHead>
      );

    if (!options.length)
      return (
        <StyledHead className={this.props.className || undefined}>
          <StyledOption>Нет статусов</StyledOption>
        </StyledHead>
      );

    return (
      <StyledOperatorStatusSelector
        {...this.props}
        key={value}
        headRenderer={this.headRenderer}
        optionRenderer={OperatorStatusSelector.optionRenderer}
        options={options}
        size={options?.length}
        value={value}
        disabled={!options.length}
        optionHeight={46}
        onChange={this.handleChange}
      />
    );
  }
}

OperatorStatusSelector.propTypes = {
  className: PropTypes.string,
  disabled: PropTypes.bool,
  onChange: PropTypes.func,
  onErrorReset: PropTypes.func,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string,
      value: PropTypes.string,
    })
  ),
  manualState: PropTypes.string,
  value: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes),
};

OperatorStatusSelector.defaultProps = {
  className: '',
  disabled: false,
  onChange: Function.prototype,
  onErrorReset: Function.prototype,
  options: null,
  errors: [],
};

const StyledOperatorStatusSelector = styled(Select)`
  width: auto;
  font-size: 12px;
  position: relative;
  line-height: normal;
  background-color: transparent;

  ${StyledSelectedOption} {
    padding: 0;
    opacity: 1;
  }

  ${StyledOptions} {
    right: 0;
    overflow: visible;
    margin-top: 10px;
    position: absolute;
    margin-right: 20px;

    &:before {
      left: auto;
      right: 15px;
    }
  }

  ${StyledOptionWrapper} {
    color: black;
    min-height: 39px;
    line-height: 39px;
    background-color: transparent;

    &[data-selected='true'] {
      background-color: #dcedff;
    }
  }

  ${StyledStatusIcon} {
    display: none;
  }
`;

const StyledHead = styled.div`
  display: flex;
  align-items: center;
  margin-left: auto;
  position: relative;
`;

const StyledThrobber = styled(Throbber)`
  margin-right: 10px;
  position: relative;
`;

const StyledOption = styled.div`
  display: flex;
  white-space: nowrap;
  padding-right: 10px;
  font-size: 14px;
  align-items: center;
`;

const StyledIndicator = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: ${props => props.color};
`;

const StyledOptionIndicator = styled(StyledIndicator)`
  margin-right: 10px;
`;

export default OperatorStatusSelector;
